import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  DeleteFaqMutationResponse,
  DeleteFaqPathParams,
} from "../types/DeleteFaq"

type DeleteFaqClient = typeof client<DeleteFaqMutationResponse, Error, never>
type DeleteFaq = {
  data: DeleteFaqMutationResponse
  error: Error
  request: never
  pathParams: DeleteFaqPathParams
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<DeleteFaqClient>>
  client: {
    parameters: Partial<Parameters<DeleteFaqClient>[0]>
    return: Awaited<ReturnType<DeleteFaqClient>>
  }
}
/**
 * @summary Delete a FAQ
 * @link /v1/faq/:id
 */
export function useDeleteFaq(
  id: DeleteFaqPathParams["id"],
  options: {
    mutation?: UseMutationOptions<
      DeleteFaq["response"],
      DeleteFaq["error"],
      DeleteFaq["request"]
    >
    client?: DeleteFaq["client"]["parameters"]
  } = {}
): UseMutationResult<
  DeleteFaq["response"],
  DeleteFaq["error"],
  DeleteFaq["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<DeleteFaq["response"], DeleteFaq["error"], never>({
    mutationFn: async () => {
      const res = await client<
        DeleteFaq["data"],
        DeleteFaq["error"],
        DeleteFaq["request"]
      >({
        method: "delete",
        url: `/v1/faq/${id}`,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
